import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Tooltip,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  Tabs,
  Tab,
  Pagination,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Edit,
  Visibility,
  Add,
  FilterList,
  Cancel,
  Delete,
  Download,
  Close,
  EventRepeat,
  ViewList,
  CalendarMonth,
  ArrowBack,
  CurrencyRupee,
  Block,
  Payments,
  StickyNote2,
  StickyNote2Outlined,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { apiService } from '../services/apiService';
import { bookingStudioOf, studioIdOf } from '../utils/studioOf';
import { colors } from '../theme/theme';
import RescheduleBookingDialog from '../components/RescheduleBookingDialog';
import { useAuth } from '../contexts/AuthContext';
import ManualBookingDialog from '../components/ManualBookingDialog';
import { PackPaymentChip } from '../components/PackBookingTooltip';
import { resolveStudioLogoUrl } from '../utils/studioLogo';
import BookingsCalendarView, { CalendarSession } from '../components/BookingsCalendarView';
import RefundsPanel from '../components/RefundsPanel';
import { isOwnerRole, isStudioScopedRole } from '../utils/roles';
import { guestBookingLabel } from '../utils/guestBooking';
import { branchLabel, locationsWithRole, useMyLocations } from '../hooks/useMyLocations';
import { ALL_LOCATIONS } from '../components/LocationSwitcher';
import BookingStatusDialog, { isPackBooking } from '../components/BookingStatusDialog';
import ScheduleCalendarView from '../components/schedule/ScheduleCalendarView';
import { SCHED, segBtnSx, segSx } from '../components/schedule/scheduleTheme';
import { noteMenuLabel, notesModeFor, useUserNotes, UserNotePopover } from '../components/UserNoteDialog';
import { noteStudioId } from '../services/userNotesApi';

// applyOffset=true for event dates (stored as IST-labeled-UTC, need offset correction)
// applyOffset=false for workout/session dates (stored as proper UTC, just convert to IST)
const formatDateIST = (dateString: string | null | undefined, format: 'date' | 'time' | 'datetime' = 'datetime', applyOffset = true): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const istOffsetMs = 5.5 * 60 * 60 * 1000;
    const adjustedDate = applyOffset ? new Date(date.getTime() - istOffsetMs) : date;
    
    if (format === 'date') {
      return adjustedDate.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'Asia/Kolkata',
      });
    } else if (format === 'time') {
      return adjustedDate.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      });
    } else {
      return adjustedDate.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      });
    }
  } catch {
    return 'N/A';
  }
};

// Compact IST timestamp: "07 May, 6:15 pm" — no year, fits in narrow columns
const formatCompactIST = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch { return ''; }
};


const effectiveBookingSource = (booking: {
  bookingSource?: string;
  transaction?: { bookingSource?: string };
  isFromWebsite?: boolean;
  isManualBooking?: boolean;
  studio?: { whitelabelConfig?: { appSource?: string } };
}): string => {
  const raw = booking.bookingSource || booking.transaction?.bookingSource;
  if (raw) return raw;
  // Legacy fallback: no bookingSource stored → infer from the studio's
  // whitelabel config when present, else assume the main mobile app.
  return booking.studio?.whitelabelConfig?.appSource || 'mobile_app';
};

interface Booking {
  _id: string;
  bookingId?: string;
  user: {
    _id: string;
    name: string;
    email: string;
    image?: string;
    phoneNumber?: string;
  };
  studio: {
    _id: string;
    studioName: string;
    /** The short local name of this branch ("Yelahanka"). Null for studios
        that have no branches, which is most of them. */
    branchName?: string | null;
    images?: string[];
    whitelabelConfig?: { appSource?: string };
  };
  session?: {
    _id: string;
    className: string;
    image?: string;
    date: string;
    price: number;
  };
  event?: {
    _id: string;
    eventName: string;
    images?: string[];
    date: string;
    price: number;
  };
  wellnessService?: {
    _id: string;
    name: string;
    duration?: number;
    price?: number;
  };
  wellnessSlotStart?: string;
  wellnessInstructors?: { _id: string; name: string }[];
  assignedInstructor?: { _id: string; name: string };
  bookingType: 'workout' | 'event' | 'trial' | 'wellness';
  bookingStatus: 'BOOKED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW' | 'CHECKED_IN' | string;
  // Refund ledger row for this booking, attached server-side. Null when no
  // money was ever capturable (pack, cash, free) — not the same as "no refund".
  refundStatus?: 'REQUESTED' | 'INITIATED' | 'PROCESSED' | 'FAILED' | 'REJECTED' | 'SKIPPED' | null;
  refund?: {
    _id: string;
    status: string;
    amount: number;
    mode: 'auto' | 'manual' | 'none';
    gateway: 'ss' | 'studio';
    reason?: string | null;
    failureReason?: string | null;
  } | null;
  isPaid?: boolean;
  orderId?: string;
  paymentId?: string;
  isFromWebsite?: boolean;
  isManualBooking?: boolean;
  isDuetBooking?: boolean;
  // Guest booking: set when a member booked this seat for `user` from their own pack.
  bookedBy?: any;
  bookedByUser?: { _id?: string; name?: string };
  guestGroupId?: string;
  bookingSource?: string;
  transaction?: {
    _id: string;
    basePrice: number;
    taxes: number;
    paymentId?: string;
    modeOfPayment?: string;
    isFromWebsite?: boolean;
    isManualBooking?: boolean;
    bookingSource?: string;
    couponCode?: string;
    coupon?: {
      _id?: string;
      code?: string;
      title?: string;
      discountType?: 'percentage' | 'amount';
      discountValue?: number;
      maxDiscountAmount?: number;
    } | null;
    promoCode?: {
      _id?: string;
      code?: string;
      value?: number;
      valueCap?: number;
      type?: 'percentage' | 'amount';
    } | null;
    purchasedPack?: {
      _id?: string;
      title?: string;
      classesLeft?: number | null;
      totalClasses?: number | null;
      expiryDate?: string;
      packEndDate?: string;
      startDate?: string;
      studio?: { studioName?: string };
      pack?: { title?: string };
    } | null;
  };
  quantity?: number;
  // 'full' / 'half' for a multi-purpose court booking; null/undefined otherwise.
  // Computed server-side in getAllBookings.
  courtSlot?: 'half' | 'full' | null;
  createdAt: string;
  updatedAt: string;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

type DiscountBreakdown = {
  code?: string;
  discountAmount: number;
  originalBase: number;
  isEstimated: boolean;
  source: 'coupon' | 'promo';
  rateType: 'percentage' | 'amount';
  rateValue: number;
};

const getSourceLabel = (source: 'coupon' | 'promo') =>
  source === 'coupon' ? 'Coupon' : 'Promo Code';

const formatDiscountRate = (b: DiscountBreakdown): string =>
  b.rateType === 'percentage' ? `${b.rateValue}% off` : `₹${b.rateValue} off`;

const getDiscountBreakdown = (b: Booking): DiscountBreakdown | null => {
  const transaction = b.transaction;
  const coupon = transaction?.coupon;
  const promo = coupon ? null : transaction?.promoCode;
  if (!coupon && !promo) return null;

  const paidBase = transaction?.basePrice ?? 0;
  const listPrice = round2((b.session?.price ?? b.event?.price ?? 0) * (b.quantity ?? 1));

  const source: 'coupon' | 'promo' = coupon ? 'coupon' : 'promo';
  const code = coupon ? coupon.code : promo!.code;
  const rateType: 'percentage' | 'amount' = coupon
    ? (coupon.discountType === 'amount' ? 'amount' : 'percentage')
    : (promo!.type === 'amount' ? 'amount' : 'percentage');
  const rateValue = coupon ? (coupon.discountValue ?? 0) : (promo!.value ?? 0);

  if (listPrice > 0) {
    const originalBase = listPrice;
    const discountAmount = Math.max(0, round2(originalBase - paidBase));
    return { code, discountAmount, originalBase, isEstimated: false, source, rateType, rateValue };
  }

  if (rateType === 'amount') {
    const discountAmount = round2(rateValue);
    const originalBase = round2(paidBase + discountAmount);
    return { code, discountAmount, originalBase, isEstimated: true, source, rateType, rateValue };
  }
  if (rateValue >= 100) {
    return { code, discountAmount: paidBase, originalBase: paidBase, isEstimated: true, source, rateType, rateValue };
  }
  const scale = 1 / (1 - rateValue / 100);
  const originalBase = round2(paidBase * scale);
  const discountAmount = round2(originalBase - paidBase);
  return { code, discountAmount, originalBase, isEstimated: true, source, rateType, rateValue };
};

const renderBookingDiscountTag = (row: Booking) => {
  const breakdown = getDiscountBreakdown(row);
  if (!breakdown) return null;

  const caption = `${breakdown.code ?? 'N/A'} · ${formatDiscountRate(breakdown)}`;
  const tooltip = `${getSourceLabel(breakdown.source)}: ${caption}`;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap={0.35}
      sx={{ py: 0.25, maxWidth: '100%' }}
      onClick={(e) => e.stopPropagation()}
    >
      <Chip
        label={caption}
        size="small"
        title={tooltip}
        sx={{
          height: 24,
          fontSize: '0.68rem',
          fontWeight: 600,
          fontFamily: 'monospace',
          border: '1px solid #80cbc4',
          color: '#00695c',
          bgcolor: '#e0f2f1',
          maxWidth: '100%',
          '& .MuiChip-label': {
            px: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: '#00695c',
          fontSize: '0.68rem',
          fontWeight: 700,
          lineHeight: 1.25,
        }}
      >
        {`${getSourceLabel(breakdown.source)} Applied`}
      </Typography>
    </Box>
  );
};

/**
 * How a refund reads in the Bookings tab.
 *
 * SKIPPED is deliberately labelled "No refund" rather than hidden: a member
 * who cancelled too late, or at a studio that does not refund, is a question
 * support will be asked, and the answer should be on the row.
 */
const REFUND_CHIP: Record<string, { label: string; color: string }> = {
  REQUESTED: { label: 'Refund requested', color: colors.warning },
  INITIATED: { label: 'Refund processing', color: colors.info },
  PROCESSED: { label: 'Refunded', color: colors.success },
  FAILED: { label: 'Refund failed', color: colors.error },
  REJECTED: { label: 'Refund declined', color: colors.textSecondary },
  SKIPPED: { label: 'No refund', color: colors.textSecondary },
};

const BookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { admin, canWriteSessions } = useAuth();
  // Brand-aware: studio and brand owners are studio roles too.
  const isStudio = isStudioScopedRole(admin?.role);
  // Studio roles get the same schedule calendar as the Sessions page; super
  // admins (no single studio) keep the cross-studio date picker below.
  const scheduleStudio: any = isStudio ? admin?.studio : null;
  const scheduleStudioId: string = scheduleStudio?._id || '';
  const scheduleOffersWellness =
    scheduleStudio?.studioPermissions?.enableWellness === true ||
    scheduleStudio?.studioType === 'wellness' ||
    (Array.isArray(scheduleStudio?.studioTypes) && scheduleStudio.studioTypes.includes('wellness'));
  const [scheduleCategory, setScheduleCategory] = useState<'workout' | 'wellness'>('workout');
  // Bumped after a booking made from the header so the calendar's counts refresh.
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);
  const isSuperAdmin = admin?.role === 'super_admin' || admin?.role === 'admin';
  // Mirrors the backend's staff branch (resolveRescheduleAccess): an active
  // SESSION booking only. Events have no same-class alternatives to move into,
  // and a cancelled booking must be restored first. Instructors are excluded
  // because AdminOrStudioGuard rejects them — offering it would only 403.
  const canReschedule = (b: Booking | null) =>
    !!b &&
    // Super admin or STUDIO OWNER only — managers and instructors are rejected
    // server-side (assertOwnerOrAdmin), so showing it to them would only 403.
    (isSuperAdmin || isOwnerRole(admin?.role)) &&
    b.bookingStatus !== 'CANCELLED' &&
    !!(b as any).session &&
    !(b as any).event;
  const isInstructor = admin?.role === 'instructor';
  // Refunds move money, so the queue is for the two roles that own that
  // decision. Managers and instructors are rejected by AdminGuard/StudioGuard
  // server-side anyway — showing them the tab would only 403.
  const canSeeRefunds = isSuperAdmin || isOwnerRole(admin?.role);
  const instructorViewAllBookings: boolean =
    isInstructor && (admin?.studio as any)?.whitelabelConfig?.instructorViewAllBookings === true;
  // Source key for this studio's whitelabelled app (e.g. 'purple_pilates') — applies to all studio roles
  const studioAppSource: string | null =
    isStudio ? ((admin?.studio as any)?.whitelabelConfig?.appSource ?? null) : null;
  // Keep instructorAppSource as an alias for backward compat
  const instructorAppSource: string | null = studioAppSource;
  const [bookings, setBookings] = useState<Booking[]>([]);
  // Member notes: any of the location's staff writes its note, super admins
  // read every studio's.
  const notesMode = notesModeFor(admin?.role);
  const { notesByUser, updateNote } = useUserNotes(bookings.map((b) => b.user?._id), notesMode);
  // The note popup on a booking row. studioId: the booking's own location —
  // a note from a booking row lands there.
  const [noteUser, setNoteUser] = useState<{
    _id: string;
    name?: string;
    studioId: string | null;
    studioLabel?: string;
    anchorEl: HTMLElement;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // searchTerm follows every keystroke (just the text box); appliedSearchRef is
  // the debounced value the SERVER was actually asked for, read by fetchBookings
  // (called from many handlers) so every reload carries the current search.
  const [searchTerm, setSearchTerm] = useState('');
  const appliedSearchRef = useRef('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [bookingToReschedule, setBookingToReschedule] = useState<Booking | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<Booking | null>(null);
  
  // Filter state
  const [studioFilter, setStudioFilter] = useState('');
  const [dateFilterType, setDateFilterType] = useState<'none' | 'month' | 'week' | 'custom'>('none');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [studios, setStudios] = useState<any[]>([]);
  // A source tab is a whitelabel appSource, or `studio:<id>` for a studio with a
  // booking website and no app of its own.
  const studioForTab = (key: string): any =>
    key.startsWith('studio:')
      ? studios.find((s: any) => s._id === key.slice(7))
      : studios.find((s: any) => s.whitelabelConfig?.appSource === key);
  // EVERY studio behind a tab. `whitelabelConfig.appSource` stopped being
  // unique per studio when a brand gained branches — Purple Pilates is two
  // studios on one app — so an app tab must cover all of them, not the first.
  const studiosForTab = (key: string): any[] =>
    key.startsWith('studio:')
      ? studios.filter((s: any) => s._id === key.slice(7))
      : studios.filter((s: any) => s.whitelabelConfig?.appSource === key);
  const [isExporting, setIsExporting] = useState(false);
  const [manualBookingOpen, setManualBookingOpen] = useState(false);
  const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);
  const [bookingToUpdateStatus, setBookingToUpdateStatus] = useState<Booking | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelCreditBack, setCancelCreditBack] = useState(true);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  // Instructors in whitelabelled studios always land on their own app's tab to hide sweatsocial bookings
  const [sourceTab, setSourceTab] = useState<string>(instructorAppSource ?? 'all');

  // List/Calendar is driven by the URL so browser/app back pops the session
  // drill-down instead of leaving /bookings (which was sending people to
  // whichever page they were on before — often Transactions).
  //   /bookings                  → unfiltered list
  //   /bookings?view=calendar    → calendar
  //   /bookings?session=<id>     → list filtered to that session
  const sessionIdParam = searchParams.get('session');
  const viewMode: 'list' | 'calendar' | 'refunds' = sessionIdParam
    ? 'list'
    : searchParams.get('view') === 'calendar'
      ? 'calendar'
      : searchParams.get('view') === 'refunds'
        ? 'refunds'
        : 'list';
  // The session picked from the calendar view — kept as the full object (not
  // just its id) so "Create Booking" can prefill it, and for super admins,
  // its studio too.
  const [selectedCalendarSession, setSelectedCalendarSession] = useState<CalendarSession | null>(null);
  // Once the calendar has been opened, keep it mounted (just hidden) instead of
  // unmounting on every toggle — otherwise its selected date and session cache
  // would reset to today each time we flip back from the filtered list.
  const [hasOpenedCalendar, setHasOpenedCalendar] = useState(false);
  useEffect(() => {
    if (viewMode === 'calendar') setHasOpenedCalendar(true);
  }, [viewMode]);

  useEffect(() => {
    if (!sessionIdParam) {
      setSelectedCalendarSession(null);
      return;
    }
    setSelectedCalendarSession((prev) => {
      if (prev?._id === sessionIdParam) return prev;
      const fromState = (location.state as { calendarSession?: CalendarSession } | null)?.calendarSession;
      if (fromState?._id === sessionIdParam) return fromState;
      return prev;
    });
  }, [sessionIdParam, location.state]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Which tab `totalCount`/`bookings` actually belong to. `bookings` is cleared the
  // instant a new tab's fetch starts, but `totalCount` intentionally isn't (to avoid
  // flashing 0) — so without this, the badge briefly shows the PREVIOUS tab's total
  // next to an already-empty table. Only render a count once this catches up to the
  // tab that's actually selected.
  const [countLoadedForTab, setCountLoadedForTab] = useState<string>(sourceTab);
  const pageSize = 100;

  // Guards against out-of-order responses: switching tabs/filters quickly fires a
  // new fetch before the previous one has resolved, and without this an older,
  // slower response can land after a newer one and silently overwrite its state —
  // the exact cause of totals flickering between values on repeat clicks.
  const fetchIdRef = useRef(0);

  // ── Branch tabs (a studio login holding 2+ locations only) ──────────────
  // One tab per location, plus "All" where the server can aggregate (2+
  // locations held as manager or above — the same rule the switcher defaults
  // on). A tab only narrows THIS list: the request is pinned to that location
  // (getBookingsFor), the app-wide switcher is untouched. null = follow the
  // switcher, which is every single-location login, exactly as before.
  const { locations: myLocations, roleByStudio, selectedLocationId } = useMyLocations();
  const showBranchTabs = isStudio && myLocations.length > 1;
  const manageable = locationsWithRole(roleByStudio, 'manager');
  const branchAllTab = manageable === null || manageable.length >= 2;
  const [branchTab, setBranchTab] = useState<string | null>(null);
  // Read by fetchBookings (called from many handlers) so every reload stays
  // on the chosen branch.
  const branchTabRef = useRef<string | null>(null);
  // What the switcher is showing. With nothing chosen and no aggregate view
  // (an instructor at two locations) that is the studio signed in to.
  const switcherBranch =
    selectedLocationId ?? (branchAllTab ? ALL_LOCATIONS : apiService.ownStudioId() ?? ALL_LOCATIONS);
  const activeBranchTab = branchTab ?? switcherBranch;
  const [countLoadedForBranch, setCountLoadedForBranch] = useState<string | null>(null);

  useEffect(() => {
    if (!isStudio) {
      fetchStudios();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sessionIdParam) {
      setStatusFilter('all_with_cancelled');
      fetchBookings(1, true, undefined, undefined, sessionIdParam);
    } else {
      setStatusFilter('all');
      fetchBookings(1, true, undefined, undefined, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdParam]);

  // A whitelabel tab (e.g. a studio's own app) can be clicked before `studios`
  // finishes loading — its studioId can't be resolved yet, so that fetch silently
  // comes back unfiltered instead of scoped to the studio, showing the wrong total
  // under that tab. Once studios load, refetch if we're still sitting on such a tab.
  useEffect(() => {
    if (isStudio || studios.length === 0) return;
    if (sourceTab === 'all' || sourceTab === 'sweatsocial') return;
    const tabStudio = studioForTab(sourceTab);
    if (tabStudio) {
      fetchBookings(1, true, undefined, sourceTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studios]);

  // After bookings load: if a studio role has no appSource configured in whitelabelConfig,
  // auto-select the first non-sweatsocial source so sweatsocial bookings are always hidden.
  useEffect(() => {
    if (!isStudio) return;
    if (studioAppSource) return; // already defaulted by useState
    if (bookings.length === 0) return;
    const firstWhitelabel = bookings.find(b => {
      const src = b.bookingSource || b.transaction?.bookingSource;
      return src && src !== 'mobile_app' && src !== 'website' && src !== 'admin';
    });
    if (firstWhitelabel) {
      const src = (firstWhitelabel.bookingSource || firstWhitelabel.transaction?.bookingSource) as string;
      setSourceTab(src);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  const fetchStudios = async () => {
    if (isStudio) {
      return; // Studios don't need to fetch all studios
    }
    try {
      const response = await apiService.getStudios();
      // Handle different response structures
      const studiosData = response.data || response.studios || [];
      const studiosList = Array.isArray(studiosData) ? studiosData : [];
      setStudios(studiosList);
    } catch (err) {
      console.error('Error fetching studios:', err);
      setStudios([]);
    }
  };

  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const getCurrentWeek = () => {
    const now = new Date();
    const year = now.getFullYear();
    // Get the first day of the year
    const startOfYear = new Date(year, 0, 1);
    // Calculate days since start of year
    const daysSinceStart = Math.floor((now.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
    // Calculate week number (ISO week starts on Monday)
    // Adjust for the day of week of Jan 1st
    const jan1Day = startOfYear.getDay();
    const weekNumber = Math.ceil((daysSinceStart + jan1Day) / 7);
    // Ensure week number is between 01 and 53
    const week = Math.min(Math.max(weekNumber, 1), 53);
    return `${year}-W${String(week).padStart(2, '0')}`;
  };

  // Returns IST-aware full ISO strings so the backend date range matches IST midnight exactly.
  const toISTStart = (yyyy: string, mm: string, dd: string) => `${yyyy}-${mm}-${dd}T00:00:00+05:30`;
  const toISTEnd   = (yyyy: string, mm: string, dd: string) => `${yyyy}-${mm}-${dd}T23:59:59.999+05:30`;

  const getDateRange = () => {
    let startDate = '';
    let endDate = '';

    if (dateFilterType === 'month' && selectedMonth) {
      const [year, month] = selectedMonth.split('-');
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      startDate = toISTStart(year, month, '01');
      endDate   = toISTEnd(year, month, String(lastDay).padStart(2, '0'));
    } else if (dateFilterType === 'week' && selectedWeek) {
      const [year, week] = selectedWeek.split('-W');
      const firstDay = new Date(parseInt(year), 0, 1);
      const days = (parseInt(week) - 1) * 7;
      const start = new Date(firstDay);
      start.setDate(firstDay.getDate() + days - firstDay.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      // Use local (IST) date parts so week boundaries are IST midnight
      const fmt = (d: Date) => [
        String(d.getFullYear()),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0'),
      ] as [string, string, string];
      startDate = toISTStart(...fmt(start));
      endDate   = toISTEnd(...fmt(end));
    } else if (dateFilterType === 'custom') {
      // Date inputs give YYYY-MM-DD in local (IST) time
      if (customStartDate) startDate = toISTStart(...(customStartDate.split('-') as [string, string, string]));
      if (customEndDate)   endDate   = toISTEnd(...(customEndDate.split('-') as [string, string, string]));
    }

    return { startDate, endDate };
  };

  const handleDateFilterTypeChange = (newType: 'none' | 'month' | 'week' | 'custom') => {
    setDateFilterType(newType);

   
    if (newType === 'month') {
      setSelectedMonth(getCurrentMonth());
    } else if (newType === 'week') {
      setSelectedWeek(getCurrentWeek());
    } else if (newType === 'none') {
      setSelectedMonth('');
      setSelectedWeek('');
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const fetchBookings = async (page: number = 1, reset: boolean = false, statusOverride?: string, sourceTabOverride?: string, sessionIdOverride?: string | null) => {
    const fetchId = ++fetchIdRef.current;
    try {
      if (reset) {
        setIsLoading(true);
        setBookings([]);
        setCurrentPage(1);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      const { startDate, endDate } = getDateRange();
      const params: any = { page, pageSize };

      // Every Status option is filtered server-side (same `status` param the
      // export sends), so count/pages match the rows shown and "load more"
      // pages through the filtered set. 'all_with_cancelled' = no filter.
      const effectiveStatus = statusOverride !== undefined ? statusOverride : statusFilter;
      if (effectiveStatus !== 'all_with_cancelled') {
        params.status = effectiveStatus;
      }

      // Tab-based source filter takes precedence over the manual studio filter in the filter dialog.
      // 'sweatsocial' tab → send bookingSource=sweatsocial (backend returns only main-app bookings).
      // Whitelabel tab → send studioId for that studio (fetches 100 bookings for that studio).
      // 'all' tab → use manual studioFilter if set.
      const effectiveSourceTab = sourceTabOverride !== undefined ? sourceTabOverride : sourceTab;
      // False only when a whitelabel tab's studioId couldn't be resolved yet (studios
      // still loading) — that request comes back unscoped, so its count must NOT be
      // attributed to this tab (the studios-loaded effect will refetch it correctly).
      let tabFilterResolved = true;
      if (effectiveSourceTab === 'sweatsocial') {
        params.bookingSource = 'sweatsocial';
      } else if (effectiveSourceTab !== 'all') {
        const tabStudios = studiosForTab(effectiveSourceTab);
        if (tabStudios.length > 1) {
          params.studioIds = tabStudios.map((s: any) => s._id).join(',');
        } else if (tabStudios[0]?._id) {
          params.studioId = tabStudios[0]._id;
        } else {
          tabFilterResolved = false;
        }
      } else {
        if (studioFilter) {
          params.studioId = studioFilter;
        }
      }

      if (startDate) {
        params.startDate = startDate;
      }

      if (endDate) {
        params.endDate = endDate;
      }

      // Instructor with instructorViewAllBookings=false: filter to their own sessions only
      if (isInstructor && !instructorViewAllBookings && admin?.instructorId) {
        params.instructorId = admin.instructorId;
      }

      // Session filter set from the calendar view — scopes this SAME list to one session's bookings
      const effectiveSessionId = sessionIdOverride !== undefined ? sessionIdOverride : (sessionIdParam || selectedCalendarSession?._id);
      if (effectiveSessionId) {
        params.sessionId = effectiveSessionId;
      }

      // Search runs on the server across ALL bookings, not just the page loaded.
      if (appliedSearchRef.current) {
        params.search = appliedSearchRef.current;
      }

      const branch = branchTabRef.current;
      const response = branch
        ? await apiService.getBookingsFor(branch, params)
        : await apiService.getBookings(params);
      // A newer fetch (another tab/filter click) has already started — this
      // response is stale, discard it instead of overwriting fresher state.
      if (fetchId !== fetchIdRef.current) return;

      if (response) {
        const newBookings = response.bookings || [];

        if (reset) {
          // Reset bookings for first page
          setBookings(newBookings);
        } else {
          // Append new bookings to existing ones
          setBookings(prev => [...prev, ...newBookings]);
        }

        setTotalPages(response.pages || 1);
        setTotalCount(response.count || 0);
        setCurrentPage(page);
        if (tabFilterResolved) {
          setCountLoadedForTab(effectiveSourceTab);
        }
        setCountLoadedForBranch(branch ?? switcherBranch);
      }
    } catch (err: any) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('Error fetching bookings:', err);
      setError(err.message || 'Failed to fetch bookings');
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  };

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      const { startDate, endDate } = getDateRange();
      const params: any = {};

      // Map UI synthetic status values to backend-understood params.
      // 'all_with_cancelled' → no param (backend returns every status)
      // 'all'               → status=all (backend excludes CANCELLED)
      // 'attended'          → status=attended (backend filters NO_SHOW + CHECKED_IN)
      // anything else       → pass through as a real status value
      if (statusFilter === 'all_with_cancelled') {
        // no status param — include every status
      } else if (statusFilter === 'all') {
        params.status = 'all';
      } else if (statusFilter === 'attended') {
        params.status = 'attended';
      } else {
        params.status = statusFilter;
      }

      // Apply the active source tab as a bookingSource filter (mirrors fetchBookings logic)
      if (sourceTab === 'sweatsocial') {
        params.bookingSource = 'sweatsocial';
      } else if (sourceTab !== 'all') {
        const tabStudios = studiosForTab(sourceTab);
        if (tabStudios.length > 1) {
          params.studioIds = tabStudios.map((s: any) => s._id).join(',');
        } else if (tabStudios[0]?._id) {
          params.studioId = tabStudios[0]._id;
        }
      }

      // Manual studio filter applies when on 'all' tab
      if (sourceTab === 'all' && studioFilter) {
        params.studioId = studioFilter;
      }

      if (startDate) {
        params.startDate = startDate;
      }

      if (endDate) {
        params.endDate = endDate;
      }

      const blob = await apiService.exportBookingsCSV(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookings-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Error exporting CSV:', err);
      setError(err.message || 'Failed to export bookings');
    } finally {
      setIsExporting(false);
    }
  };

  const handleApplyFilters = () => {
    setFilterDialogOpen(false);
    fetchBookings(1, true);
  };

  const handleClearFilters = () => {
    setStudioFilter('');
    setDateFilterType('none');
    setCustomStartDate('');
    setCustomEndDate('');
    setSelectedMonth('');
    setSelectedWeek('');
    setFilterDialogOpen(false);
    fetchBookings(1, true);
  };


  const handleSelectSession = (session: CalendarSession) => {
    setSelectedCalendarSession(session);
    setStatusFilter('all_with_cancelled');
    // Clear any search left over from the list view — otherwise it keeps
    // filtering this session's bookings and can hide all of them if the old
    // search text doesn't match anyone in this specific session.
    resetSearch();
    // Push a history entry so Back returns to the calendar instead of the
    // previous sidebar page (Transactions sits right under Bookings).
    setSearchParams({ session: session._id }, { state: { calendarSession: session } });
  };

  const handleBackToSessions = () => {
    setSelectedCalendarSession(null);
    setStatusFilter('all');
    setSearchParams({ view: 'calendar' }, { replace: true });
  };

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    next: 'list' | 'calendar' | 'refunds' | null,
  ) => {
    if (!next) return;
    // A search typed in one view has no reason to keep filtering another —
    // most obviously, it can hide every booking in a session opened from the
    // calendar just because the old search text doesn't match anyone there.
    // The loaded rows were server-filtered by that search, so reload them
    // unfiltered — nothing else will when we come back to the list.
    if (resetSearch()) fetchBookings(1, true);
    if (next === 'calendar' || next === 'refunds') {
      if (sessionIdParam) {
        handleBackToSessions();
        if (next === 'refunds') setSearchParams({ view: 'refunds' }, { replace: true });
        return;
      }
      setSearchParams({ view: next }, { replace: true });
      return;
    }
    setSearchParams({}, { replace: true });
  };

  // Drops the search WITHOUT refetching. Returns whether the loaded rows were
  // server-filtered by it, so the caller can reload if nothing else will.
  const resetSearch = (): boolean => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const hadAppliedSearch = appliedSearchRef.current !== '';
    setSearchTerm('');
    appliedSearchRef.current = '';
    return hadAppliedSearch;
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    // Wait for a pause in typing, then search the server once (not per keystroke).
    searchDebounceRef.current = setTimeout(() => {
      const next = value.trim();
      if (next === appliedSearchRef.current) return;
      appliedSearchRef.current = next;
      fetchBookings(1, true);
    }, 400);
  };

  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  const handleStatusFilter = (event: any) => {
    const newStatus = event.target.value;
    setStatusFilter(newStatus);
    fetchBookings(1, true, newStatus);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, booking: Booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBooking(null);
  };

  const handleViewBooking = () => {
    if (selectedBooking) {
      navigate(`/bookings/${selectedBooking._id}`);
    }
    handleMenuClose();
  };

  // ── Refund actions ────────────────────────────────────────────────────────
  // Only ever offered on a booking that already HAS a ledger row in a state a
  // human can act on. A cancellation that missed the studio's window is
  // recorded SKIPPED and deliberately gets no action here — there is nothing
  // to approve, and offering it would invite paying out a refund the policy
  // says is not owed.
  const [refundBusy, setRefundBusy] = useState(false);
  const [rejectRefundOpen, setRejectRefundOpen] = useState(false);
  const [rejectRefundReason, setRejectRefundReason] = useState('');
  const [refundTarget, setRefundTarget] = useState<Booking | null>(null);

  // ── Mark as paid ──────────────────────────────────────────────────────────
  // Super admin or STUDIO OWNER only — the server rejects managers/instructors.
  // Same "owed" rule as the Payment column's Unpaid/Pending chip.
  const canMarkPaid = (b: Booking | null) => {
    if (!b || !(isSuperAdmin || isOwnerRole(admin?.role))) return false;
    const mode = b.transaction?.modeOfPayment;
    const amount = (b.transaction?.basePrice ?? 0) + (b.transaction?.taxes ?? 0);
    return (
      b.isPaid === false &&
      b.bookingStatus !== 'CANCELLED' &&
      mode !== 'free' &&
      mode !== 'subscription' &&
      amount > 0
    );
  };

  // The row's "Mark paid" button opens this confirmation (no menu, no detail page).
  const [markPaidTarget, setMarkPaidTarget] = useState<Booking | null>(null);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  const handleMarkPaidConfirm = async () => {
    const target = markPaidTarget;
    if (!target) return;
    setIsMarkingPaid(true);
    try {
      await apiService.markBookingPaid(target.bookingId || target._id);
      setBookings(prev => prev.map(b => (b._id === target._id ? { ...b, isPaid: true } : b)));
      setError(null);
      setMarkPaidTarget(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.data?.message || err.message || 'Failed to mark as paid');
      setMarkPaidTarget(null);
    } finally {
      setIsMarkingPaid(false);
    }
  };

  const refundActionable = (b: Booking | null) =>
    b?.refund?._id && (b.refundStatus === 'REQUESTED' || b.refundStatus === 'FAILED');

  const handleProcessRefund = async () => {
    const target = selectedBooking;
    handleMenuClose();
    if (!target?.refund?._id) return;
    setRefundBusy(true);
    try {
      await apiService.processRefund(
        target.refund._id,
        studioIdOf((target.refund as any).studio) || bookingStudioOf(target),
      );
      setError(null);
      await fetchBookings(currentPage, true);
    } catch (err: any) {
      setError(err?.data?.message || err.message || 'Failed to process refund');
    } finally {
      setRefundBusy(false);
    }
  };

  const handleRejectRefundClick = () => {
    setRefundTarget(selectedBooking);
    setRejectRefundReason('');
    setRejectRefundOpen(true);
    handleMenuClose();
  };

  const handleConfirmRejectRefund = async () => {
    if (!refundTarget?.refund?._id) return;
    setRefundBusy(true);
    try {
      await apiService.rejectRefund(
        refundTarget.refund._id,
        rejectRefundReason.trim() || undefined,
        studioIdOf((refundTarget.refund as any).studio) || bookingStudioOf(refundTarget),
      );
      setRejectRefundOpen(false);
      setRefundTarget(null);
      setError(null);
      await fetchBookings(currentPage, true);
    } catch (err: any) {
      setError(err?.data?.message || err.message || 'Failed to reject refund');
    } finally {
      setRefundBusy(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!selectedBooking) return;
    setIsCancelling(true);
    try {
      // Pinned to the booking's own location — under "All locations" the
      // switcher sends __all__, which the server refuses for writes.
      await apiService.updateBookingStatus(selectedBooking._id, 'CANCELLED', cancelReason || undefined, isPackBooking(selectedBooking) ? cancelCreditBack : undefined, bookingStudioOf(selectedBooking));
      setBookings(bookings.map(b =>
        b._id === selectedBooking._id ? { ...b, bookingStatus: 'CANCELLED' as const } : b,
      ));
      setCancelDialogOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to cancel booking');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDeleteClick = () => {
    if (selectedBooking) {
      setBookingToDelete(selectedBooking);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (bookingToDelete) {
      try {
        await apiService.deleteBooking(bookingToDelete._id);
        setBookings(bookings.filter(booking => booking._id !== bookingToDelete._id));
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to delete booking');
      }
    }
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  const handleRescheduleClick = () => {
    if (selectedBooking) {
      setBookingToReschedule(selectedBooking);
      setRescheduleDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleStatusUpdateClick = () => {
    if (selectedBooking) {
      setBookingToUpdateStatus(selectedBooking);
      setStatusUpdateDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleStatusUpdated = (bookingId: string, newStatus: string) => {
    setBookings(prev => prev.map(b =>
      b._id === bookingId
        ? { ...b, bookingStatus: newStatus as Booking['bookingStatus'] }
        : b,
    ));
    setStatusUpdateDialogOpen(false);
    setBookingToUpdateStatus(null);
  };

  const getStatusColor = (status: string) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
      case 'booked':
      case 'confirmed':
        return colors.success;
      case 'pending':
        return colors.warning;
      case 'cancelled':
        return colors.error;
      case 'completed':
        return colors.info;
      case 'no_show':
        return colors.textSecondary;
      default:
        return colors.textSecondary;
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return colors.success;
      case 'free':
        return '#7c3aed';
      case 'coupon':
        return '#0d9488';
      case 'promo':
        return '#0891b2';
      case 'cash':
        return colors.info;
      case 'online':
        return colors.success;
      case 'pending':
      case 'unpaid':
      case 'due':
        return colors.warning;
      case 'notdue':
        return colors.textSecondary;
      case 'failed':
        return colors.error;
      case 'refunded':
        return colors.textSecondary;
      default:
        return colors.textSecondary;
    }
  };

  // A studio with its own booking website but no app (e.g. Vyāna) gets its own tab
  // too, keyed `studio:<id>` since it has no appSource. Its bookings are not
  // SweatSocial's (the backend's sweatsocial filter excludes it the same way).
  const ownsBookingWebsite = (s: any) =>
    s?.whitelabelConfig?.enableBookingWebsite === true && !s?.whitelabelConfig?.appSource;

  // Build whitelabel source tabs from studios list (so all tabs are always visible,
  // not just sources present in the current 100-booking batch).
  // Fall back to deriving from loaded bookings if studios list isn't populated.
  const whitelabelSources = useMemo(() => {
    if (studios.length > 0) {
      const seen = new Set<string>();
      studios.forEach(s => {
        const src = s.whitelabelConfig?.appSource;
        if (src && src !== 'mobile_app' && src !== 'website' && src !== 'admin') seen.add(src);
      });
      studios.forEach(s => {
        if (ownsBookingWebsite(s)) seen.add(`studio:${s._id}`);
      });
      return Array.from(seen);
    }
    const seen = new Set<string>();
    bookings.forEach(b => {
      const src = effectiveBookingSource(b);
      if (src && src !== 'mobile_app' && src !== 'website' && src !== 'admin') seen.add(src);
    });
    return Array.from(seen);
  }, [studios, bookings]);


  const sourceTabLabel = (key: string): string => {
    if (key.startsWith('studio:')) {
      const st = studios.find((s: any) => s._id === key.slice(7));
      return st?.studioName ?? 'Studio website';
    }
    return bookingSourceLabel(key);
  };

  const isSweatsocialBooking = (b: Booking) => {
    if ((b as any).studio?.whitelabelConfig?.enableBookingWebsite === true) return false;
    const src = effectiveBookingSource(b);
    return !src || src === 'mobile_app' || src === 'website' || src === 'admin';
  };

  // One wording for the Source column and the filters. A manual booking at a
  // studio without its own app is stored as 'admin' — it was made BY THE
  // STUDIO, so it must not read as the SweatSocial app.
  const bookingSourceLabel = (src?: string): string => {
    if (!src || src === 'mobile_app') return 'SweatSocial App';
    if (src === 'website') return 'SweatSocial Website';
    if (src === 'admin') return 'Studio';
    if (src === 'studio_web') return 'Studio Website';
    if (src === 'partner_api') return 'Partner API';
    // A studio's own app: purple_pilates → Purple Pilates.
    return src.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const filteredBookings = bookings.filter(booking => {
    if (!booking) return false;

    const matchesStatus =
      statusFilter === 'all_with_cancelled'
        ? true
        : statusFilter === 'all'
        ? booking.bookingStatus !== 'CANCELLED'
        : statusFilter === 'attended'
        ? booking.bookingStatus === 'NO_SHOW' || booking.bookingStatus === 'CHECKED_IN'
        : statusFilter === 'trial'
        ? booking.bookingType === 'trial'
        : booking.bookingStatus === statusFilter;

    // 'all' and whitelabel-studio tabs are already correctly scoped server-side
    // (via studioId, or no filter at all) — a studio's bookings can legitimately
    // have bookingSource values like 'mobile_app'/'website'/'admin' rather than
    // literally equalling its own appSource slug, so re-checking bookingSource
    // here double-filtered and wrongly excluded most of a studio's own bookings
    // from its own tab. Only 'sweatsocial' still needs a client-side re-check,
    // since it's a synthetic grouping (not a real field) the server infers too.
    const matchesSource = sourceTab === 'sweatsocial'
      ? isSweatsocialBooking(booking)
      : true;

    // Search itself is applied by the server (debounced, see handleSearch), so
    // the loaded rows already match it — nothing text-filters per keystroke here.
    return matchesStatus && matchesSource;
  });

  /**
   * The member-note icon after a booking row's name — the note at that
   * booking's location. Pink when there is one (hover to read it); faint
   * otherwise, firming up on row hover, for studio staff to add one. Opens a
   * small popover in place.
   */
  const renderNoteIcon = (row: Booking) => {
    const u = row.user;
    const at = bookingStudioOf(row);
    const note = u?._id && at ? (notesByUser[u._id] || []).find((n) => noteStudioId(n) === at) : undefined;
    if (!u?._id || !notesMode || (!note && notesMode !== 'studio')) return null;
    const Icon = note ? StickyNote2 : StickyNote2Outlined;
    return (
      <Tooltip
        title={note ? <span style={{ whiteSpace: 'pre-wrap' }}>{note.text}</span> : 'Add note'}
        placement="top"
        enterDelay={300}
      >
        <IconButton
          size="small"
          className={note ? undefined : 'note-add-icon'}
          onClick={(e) => {
            e.stopPropagation();
            setNoteUser({
              _id: u._id,
              name: u.name,
              studioId: at,
              studioLabel: row.studio?.studioName,
              anchorEl: e.currentTarget,
            });
          }}
          sx={{
            p: 0.5,
            ml: 0.25,
            flexShrink: 0,
            color: note ? colors.primary : colors.textSecondary,
            opacity: note ? 1 : 0.45,
            transition: 'opacity 120ms, background-color 120ms',
            '&:hover': { backgroundColor: `${colors.primary}1a` },
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'user',
      headerName: 'User',
      width: 220,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
          <Avatar
            src={params.value?.image}
            sx={{ width: 32, height: 32, flexShrink: 0 }}
          >
            {params.value?.name?.charAt(0) || 'U'}
          </Avatar>
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
              <Typography 
                variant="body2" 
                fontWeight="bold"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {params.value?.name || 'N/A'}
              </Typography>
              {renderNoteIcon(params.row)}
            </Box>
            <Typography 
              variant="caption" 
              color={colors.textSecondary}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {params.value?.email || 'N/A'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      width: 150,
      valueGetter: (value, row: Booking) => row.user?.phoneNumber,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'studio',
      headerName: 'Studio',
      width: 160,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
          <Avatar
            src={resolveStudioLogoUrl(params.value)}
            sx={{ width: 28, height: 28, flexShrink: 0 }}
          >
            {params.value?.studioName?.charAt(0) || 'S'}
          </Avatar>
          {/* Branch first, studio underneath.
              Two branches of one brand often share most of their name —
              "Purple Pilates" and "Purple Pilates Yelahanka" — and the column
              ellipsises at 160px, so the part that TELLS THEM APART was the
              part being cut off. The branch name is the answer to "which one
              is this", so it leads; the studio name stays as the subtitle for
              anyone who does not recognise the branch.
              Studios with no branchName are unchanged: one line, as before. */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.25,
              }}
            >
              {params.value?.branchName || params.value?.studioName || 'N/A'}
            </Typography>
            {params.value?.branchName && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                {params.value?.studioName}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: 'bookingType',
      headerName: 'Type',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
            <Typography
              variant="body2"
              fontWeight="bold"
              textTransform="capitalize"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {params.value}
            </Typography>
            {params.value === 'trial' && (
              <Chip
                label="Trial"
                size="small"
                sx={{
                  backgroundColor: '#4caf50',
                  color: colors.white,
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
            )}
            {(params.row.courtSlot === 'full' || params.row.courtSlot === 'half') && (
              <Chip
                label={params.row.courtSlot === 'full' ? 'Full court' : 'Half court'}
                size="small"
                sx={{
                  backgroundColor: params.row.courtSlot === 'full' ? '#5c6bc0' : '#26a69a',
                  color: colors.white,
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
            )}
          </Box>
          <Typography
            variant="caption"
            color={colors.textSecondary}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {params.row.session?.className || params.row.event?.eventName || params.row.wellnessService?.name || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'sessionDate',
      headerName: 'Session',
      description: 'Switch to Calendar view to browse sessions by date and see all bookings for one class',
      width: 140,
      valueGetter: (value, row: Booking) =>
        row.session?.date || row.event?.date || row.wellnessSlotStart,
      renderCell: (params) => {
        const booking = params.row as Booking;
        const date =
          booking.session?.date || booking.event?.date || booking.wellnessSlotStart;
        const isWorkout = booking.bookingType === 'workout' || booking.bookingType === 'trial';
        // Only EVENT dates are stored IST-labeled-UTC (need the -5:30 offset).
        // Sessions/trials AND wellness slots are stored as proper UTC → just
        // convert to IST (no offset). Without excluding wellness here it would be
        // double-shifted and render the raw UTC time (e.g. 04:30 instead of 10:00).
        const applyOffset = !isWorkout && booking.bookingType !== 'wellness';
        if (!date) return <Typography variant="body2">N/A</Typography>;

        return (
          <Box sx={{ py: 1 }}>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formatDateIST(date, 'date', applyOffset)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'nowrap' }}>
              <Typography
                variant="caption"
                color={colors.textSecondary}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {formatDateIST(date, 'time', applyOffset)}
              </Typography>
              {(booking.quantity ?? 1) > 1 && !booking.courtSlot && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 0.8,
                    py: 0.1,
                    borderRadius: 99,
                    backgroundColor: '#ede9fe',
                    color: '#7c3aed',
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    lineHeight: 1.6,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {booking.quantity} tickets
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 110,
      valueGetter: (value, row: Booking) => {
        const transaction = row.transaction;
        const session = row.session;
        const event = row.event;
        const wellnessService = row.wellnessService;

        if (transaction) {
          return transaction.basePrice + (transaction.taxes || 0);
        }
        return session?.price || event?.price || wellnessService?.price || 0;
      },
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          fontWeight="bold" 
          color={colors.success}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          ₹{params.value?.toLocaleString() || '0'}
        </Typography>
      ),
    },
    {
      field: 'bookingStatus',
      headerName: 'Status',
      width: 175,
      renderCell: (params) => {
        const status: string = params.value;
        const showTime = status === 'CHECKED_IN' || status === 'NO_SHOW';
        const timeLabel = status === 'CHECKED_IN' ? 'Checked in' : 'No show';
        const updatedAt: string | undefined = (params.row as Booking).updatedAt;
        return (
          <Box>
            <Box display="flex" gap={0.5} alignItems="center">
              <Chip
                label={status}
                size="small"
                sx={{
                  backgroundColor: getStatusColor(status?.toLowerCase()),
                  color: colors.white,
                  textTransform: 'capitalize',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              {(params.row as Booking).isDuetBooking && (
                <Chip
                  label="+1"
                  size="small"
                  sx={{ backgroundColor: '#7c3aed20', color: '#7c3aed', fontWeight: 700, fontSize: '0.65rem', height: 20 }}
                />
              )}
            </Box>
            {(() => {
              const guest = guestBookingLabel(params.row);
              return guest ? (
                <Chip
                  label={guest}
                  size="small"
                  sx={{ mt: 0.4, height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#00838F20', color: '#00838F' }}
                />
              ) : null;
            })()}
            {(() => {
              // Where a cancelled booking's MONEY got to. Without this the tab
              // says "Cancelled" and stops, which is exactly the blind spot
              // that left refunds untracked.
              const refund = (params.row as Booking).refund;
              const rs = (params.row as Booking).refundStatus;
              if (!rs || !refund) return null;
              const style = REFUND_CHIP[rs] ?? null;
              if (!style) return null;
              return (
                <Tooltip
                  title={
                    refund.failureReason ||
                    refund.reason ||
                    `₹${refund.amount?.toLocaleString?.() ?? refund.amount} · ${refund.mode} · ${refund.gateway === 'ss' ? 'SweatSocial account' : 'studio account'}`
                  }
                >
                  <Chip
                    label={style.label}
                    size="small"
                    sx={{
                      mt: 0.4,
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      backgroundColor: `${style.color}20`,
                      color: style.color,
                    }}
                  />
                </Tooltip>
              );
            })()}
            {showTime && updatedAt && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.3, lineHeight: 1.3, fontSize: '0.68rem' }}>
                {timeLabel}: {formatCompactIST(updatedAt)}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment',
      width: 190,
      valueGetter: (value, row: Booking) => {
        const mode = row.transaction?.modeOfPayment;
        const amount = (row.transaction?.basePrice ?? 0) + (row.transaction?.taxes ?? 0);
        const breakdown = getDiscountBreakdown(row);

        // `isPaid: false` means the studio is still owed for this booking, and
        // the backend BLOCKS the member from booking anything else until it is
        // cleared. Only the `online` branch used to check it, so an unpaid
        // cash or free walk-in rendered as a plain "Cash"/"Free" chip — staff
        // could not see what the member was blocked on, let alone clear it.
        const unpaid = row.isPaid === false;

        if (mode === 'subscription') return 'pack';
        if (breakdown && amount === 0) return breakdown.source === 'promo' ? 'promo' : 'coupon';
        if (mode === 'free') return unpaid ? 'unpaid' : 'free';
        // Pay at studio / manual cash: owed until marked paid. A cancelled one
        // is never owed, so it reads "Not due" rather than an amount owing.
        if (mode === 'cash' && unpaid) return row.bookingStatus === 'CANCELLED' ? 'notdue' : 'due';
        if (mode === 'cash') return 'cash';
        if (mode === 'online') return row.isPaid ? 'online' : 'pending';
        if (row.paymentId || row.transaction?.paymentId) return 'paid';
        if (row.orderId) return 'pending';
        return 'pending';
      },
      renderCell: (params) => {
        const labelMap: Record<string, string> = {
          pack: 'Pack Booking',
          free: 'Free',
          coupon: 'Coupon',
          promo: 'Promo Code',
          cash: 'Cash',
          online: 'Online',
          paid: 'Paid',
          pending: 'Pending',
          unpaid: 'Unpaid',
          due: 'Due',
          notdue: 'Not due',
          failed: 'Failed',
          refunded: 'Refunded',
        };
        if (params.value === 'pack') {
          return (
            <PackPaymentChip
              pack={params.row.transaction?.purchasedPack}
              // The booking's own location, so the tooltip can say when the
              // pack came from a different branch of the same brand.
              bookingLocation={params.row.studio}
              label={labelMap.pack}
            />
          );
        }
        const chip = (
          <Chip
            label={labelMap[params.value] ?? params.value}
            size="small"
            sx={{
              backgroundColor: getPaymentStatusColor(params.value),
              color: colors.white,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
        );
        if (!canMarkPaid(params.row)) return chip;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%' }}>
            {chip}
            <Button
              size="small"
              variant="outlined"
              startIcon={<Payments sx={{ fontSize: 16 }} />}
              onClick={(e) => {
                e.stopPropagation(); // the row itself opens the booking
                setMarkPaidTarget(params.row);
              }}
              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.1, px: 1, minWidth: 0, whiteSpace: 'nowrap' }}
            >
              Mark paid
            </Button>
          </Box>
        );
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 145,
      valueGetter: (value, row: Booking) => {
        const src = effectiveBookingSource(row);
        // Only bookingSource 'studio_web' is a studio's own booking website. The
        // old isFromWebsite flag is SweatSocial's website (it rides on
        // mobile_app / no source); Partner API keeps its own label.
        if (src === 'studio_web') return 'Studio Website';
        if ((row.isFromWebsite || row.transaction?.isFromWebsite) && src !== 'partner_api') return 'SweatSocial Website';
        return bookingSourceLabel(src);
      },
      renderCell: (params) => {
        const isManual = params.row.isManualBooking || params.row.transaction?.isManualBooking;
        const isWebsite = params.row.isFromWebsite || params.row.transaction?.isFromWebsite;
        const src = effectiveBookingSource(params.row);
        const isPartnerApi = src === 'partner_api';
        const isWhitelabel = !isWebsite && !isPartnerApi && src && !['mobile_app', 'website', 'admin', 'studio_web'].includes(src);
        const isStudioWeb = src === 'studio_web';
        const label = params.value as string;
        const bgColor = isWebsite || isStudioWeb ? colors.info
          : isPartnerApi ? '#0e7490'
          : isWhitelabel ? '#b45309'
          : colors.textSecondary;
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            {isManual && (
              <Box sx={{
                width: 17, height: 17, borderRadius: '50%',
                backgroundColor: '#7c3aed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.58rem', fontWeight: 800, color: '#fff',
                flexShrink: 0, letterSpacing: 0,
              }}>
                M
              </Box>
            )}
            <Chip
              label={label}
              size="small"
              sx={{
                backgroundColor: bgColor,
                color: colors.white,
                fontWeight: 600,
                fontSize: '0.72rem',
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Booked On',
      width: 130,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color={colors.textSecondary}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {formatDateIST(params.value, 'date', false)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 70,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<MoreVert />}
          label="More"
          onClick={(event) => handleMenuOpen(event, params.row)}
        />,
      ],
    },
    {
      field: 'discountTag',
      headerName: 'Discount',
      flex: 1,
      minWidth: 160,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => renderBookingDiscountTag(params.row as Booking),
    },
  ];

  const BookingsTableSkeleton = () => (
    <Box>
      {/* Column header skeleton */}
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        px={2}
        py={1.5}
        sx={{ borderBottom: `2px solid ${colors.primary}`, bgcolor: colors.backgroundTertiary }}
      >
        {[220, 180, 130, 110, 110, 110, 150].map((w, i) => (
          <Skeleton key={i} variant="text" width={w} height={20} />
        ))}
      </Box>
      {/* Row skeletons */}
      {Array.from({ length: 9 }).map((_, i) => (
        <Box
          key={i}
          display="flex"
          alignItems="center"
          gap={2}
          px={2}
          py={1.5}
          sx={{ borderBottom: `1px solid ${colors.divider}` }}
        >
          {/* User cell: avatar + two lines */}
          <Box display="flex" alignItems="center" gap={1} width={220} flexShrink={0}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box>
              <Skeleton variant="text" width={100} height={16} />
              <Skeleton variant="text" width={130} height={14} />
            </Box>
          </Box>
          {/* Session */}
          <Box width={180} flexShrink={0}>
            <Skeleton variant="text" width={140} height={16} />
            <Skeleton variant="text" width={100} height={14} />
          </Box>
          {/* Date */}
          <Skeleton variant="text" width={130} height={16} />
          {/* Status chip */}
          <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: 4 }} />
          {/* Amount */}
          <Skeleton variant="text" width={70} height={16} />
          {/* Source chip */}
          <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: 4 }} />
          {/* Booking ID */}
          <Skeleton variant="text" width={130} height={14} />
        </Box>
      ))}
    </Box>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold" color={colors.textPrimary}>
          Bookings Management
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            sx={{
              bgcolor: colors.backgroundTertiary,
              borderRadius: 2,
              p: 0.5,
              '& .MuiToggleButtonGroup-grouped': {
                border: 0,
                borderRadius: '8px !important',
                mx: 0.25,
                px: 2,
                py: 1,
                textTransform: 'none',
                alignItems: 'flex-start',
                '&.Mui-selected': {
                  bgcolor: colors.white,
                  color: colors.textPrimary,
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                  '&:hover': { bgcolor: colors.white },
                  '& svg': { color: colors.primary },
                },
                '&:not(.Mui-selected)': {
                  color: colors.textSecondary,
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                },
              },
            }}
          >
            <ToggleButton value="list" sx={{ minWidth: 168 }}>
              <Box sx={{ textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <ViewList fontSize="small" />
                  <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                    List
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 0.35, pl: 2.75, lineHeight: 1.35, opacity: 0.85 }}
                >
                  {isSuperAdmin ? 'All bookings across studios' : 'All your studio\'s bookings'}
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="calendar" sx={{ minWidth: 168 }}>
              <Box sx={{ textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <CalendarMonth fontSize="small" />
                  <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                    Calendar
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 0.35, pl: 2.75, lineHeight: 1.35, opacity: 0.85 }}
                >
                  {scheduleStudioId ? 'Day, week or month schedule' : 'Pick a date, then a session'}
                </Typography>
              </Box>
            </ToggleButton>
            {/* Refunds live here rather than in the left nav: a refund is only
                ever a fact about a cancelled booking. */}
            {canSeeRefunds && (
              <ToggleButton value="refunds" sx={{ minWidth: 168 }}>
                <Box sx={{ textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CurrencyRupee fontSize="small" />
                    <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                      Refunds
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.35, pl: 2.75, lineHeight: 1.35, opacity: 0.85 }}
                  >
                    Approve or decline cancellations
                  </Typography>
                </Box>
              </ToggleButton>
            )}
          </ToggleButtonGroup>
          {!isInstructor && (
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ borderRadius: 2 }}
              onClick={() => setManualBookingOpen(true)}
            >
              Create Booking
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {hasOpenedCalendar && (
        <Box sx={{ display: viewMode === 'calendar' ? 'block' : 'none' }}>
          {scheduleStudioId ? (
            <Box sx={{ backgroundColor: SCHED.canvas, borderRadius: '20px', p: { xs: '16px', md: '24px 28px' } }}>
              {scheduleOffersWellness && (
                <Box role="group" aria-label="Category" sx={{ ...segSx, display: 'inline-flex', mb: '16px' }}>
                  {(['workout', 'wellness'] as const).map((c) => (
                    <Box key={c} component="button" type="button" aria-pressed={scheduleCategory === c} onClick={() => setScheduleCategory(c)} sx={segBtnSx(scheduleCategory === c)}>
                      {c === 'workout' ? 'Workout' : 'Wellness'}
                    </Box>
                  ))}
                </Box>
              )}
              <ScheduleCalendarView
                studioId={scheduleStudioId}
                categoryFilter={scheduleCategory}
                refreshKey={scheduleRefreshKey}
                canWriteSessions={canWriteSessions}
              />
            </Box>
          ) : (
            <BookingsCalendarView onSelectSession={handleSelectSession} />
          )}
        </Box>
      )}
      {viewMode === 'refunds' && canSeeRefunds && (
        <RefundsPanel studioId={isSuperAdmin ? studioFilter || undefined : undefined} isSuperAdmin={isSuperAdmin} />
      )}

      {viewMode === 'list' && (
      <>
      {sessionIdParam && (
        <Alert
          severity="info"
          onClick={handleBackToSessions}
          sx={{ mb: 2, cursor: 'pointer' }}
          icon={<ArrowBack fontSize="small" />}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleBackToSessions();
              }}
            >
              Back to sessions
            </Button>
          }
        >
          Showing bookings for session
          {selectedCalendarSession && (
            <>: <strong>{selectedCalendarSession.className} • {formatDateIST(selectedCalendarSession.date, 'datetime', false)}</strong></>
          )}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              fullWidth
              placeholder="Search bookings by user, phone, email, studio, or class..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilter}
              >
                <MenuItem value="attended">No Show &amp; Checked In</MenuItem>
                <MenuItem value="all_with_cancelled">All</MenuItem>
                <MenuItem value="all">All (except cancellation)</MenuItem>
                <MenuItem value="BOOKED">Booked</MenuItem>
                <MenuItem value="trial">Trial</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                <MenuItem value="CHECKED_IN">Checked In</MenuItem>
                <MenuItem value="NO_SHOW">No Show</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              sx={{ minWidth: 120 }}
              onClick={() => setFilterDialogOpen(true)}
            >
              More Filters
            </Button>
            {isSuperAdmin && (
              <Button
                variant="contained"
                startIcon={<Download />}
                sx={{ minWidth: 120 }}
                onClick={handleExportCSV}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export Excel'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Branch Tabs — a studio login holding several locations. */}
      {showBranchTabs && (
        <Tabs
          value={activeBranchTab}
          onChange={(_, v) => {
            setBranchTab(v);
            branchTabRef.current = v;
            fetchBookings(1, true);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', maxWidth: '100%' }}
        >
          {[
            ...(branchAllTab || activeBranchTab === ALL_LOCATIONS
              ? [{ id: ALL_LOCATIONS, label: 'All' }]
              : []),
            ...myLocations.map((l) => ({ id: l._id, label: branchLabel(l) })),
          ].map(({ id, label }) => (
            <Tab
              key={id}
              value={id}
              label={
                activeBranchTab === id && countLoadedForBranch === id
                  ? `${label} (${totalCount})`
                  : label
              }
            />
          ))}
        </Tabs>
      )}

      {/* Source Tabs — platform admins only; every studio-scoped role sees its own studio(s). */}
      {!isStudioScopedRole(admin?.role) && (
        <Tabs
          value={sourceTab}
          onChange={(_, v) => { setSourceTab(v); fetchBookings(1, true, undefined, v); }}
          // One tab per app / booking-website studio — more than fit on a laptop
          // screen, so scroll sideways (arrows, trackpad or shift+wheel).
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', maxWidth: '100%' }}
        >
          {/* `bookings` only ever holds the currently-loaded page for whichever ONE
              tab was last fetched (and briefly empties on every fetch) — it can't
              give an accurate count for every tab at once. `totalCount` is the real,
              backend-computed total, but only for whichever tab `countLoadedForTab`
              says it was fetched for — checking both, not just the active tab, is
              what stops the badge from showing a stale PREVIOUS tab's number while
              the new tab's data is still loading. */}
          <Tab
            label={sourceTab === 'all' && countLoadedForTab === 'all' ? `All (${totalCount})` : 'All'}
            value="all"
          />
          {!isStudio && (
            <Tab
              label={
                sourceTab === 'sweatsocial' && countLoadedForTab === 'sweatsocial'
                  ? `Sweat Social (${totalCount})`
                  : 'Sweat Social'
              }
              value="sweatsocial"
            />
          )}
          {whitelabelSources.map(src => (
            <Tab
              key={src}
              label={
                sourceTab === src && countLoadedForTab === src
                  ? `${sourceTabLabel(src)} (${totalCount})`
                  : sourceTabLabel(src)
              }
              value={src}
            />
          ))}
        </Tabs>
      )}

      {/* Bookings Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <BookingsTableSkeleton />
          ) : (
          <Box height={650}>
            <DataGrid
              rows={filteredBookings}
              columns={columns}
              getRowId={(row) => row._id}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 100 },
                },
              }}
              onRowClick={(params) => navigate(`/bookings/${params.row._id}`)}
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              getEstimatedRowHeight={() => 70}
              getRowClassName={(params) => {
                if (params.row.bookingStatus === 'CANCELLED') return 'cancelled-booking-row';
                if (params.row.bookingStatus === 'NO_SHOW') return 'no-show-booking-row';
                return '';
              }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: `1px solid ${colors.divider}`,
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  paddingTop: '14px',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: colors.backgroundTertiary,
                  },
                  '&:hover .note-add-icon': { opacity: 1 },
                  '&.cancelled-booking-row': {
                    backgroundColor: '#ffcdd2 !important',
                    '&:hover': { backgroundColor: '#ef9a9a !important' },
                    '& .MuiDataGrid-cell': { borderBottom: `1px solid #e57373` },
                  },
                  '&.no-show-booking-row': {
                    backgroundColor: '#f5f5f5 !important',
                    '&:hover': { backgroundColor: '#eeeeee !important' },
                    '& .MuiDataGrid-cell': { borderBottom: `1px solid #bdbdbd` },
                  },
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: colors.backgroundTertiary,
                  borderBottom: `2px solid ${colors.primary}`,
                  minHeight: '56px !important',
                  maxHeight: '56px !important',
                  lineHeight: '56px !important',
                },
                '& .MuiDataGrid-columnHeader': {
                  padding: '12px 16px',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 600,
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
          )}

          {/* Pagination */}
          {!isLoading && totalPages > 1 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, borderTop: `1px solid ${colors.divider}` }}>
              {isLoadingMore && <CircularProgress size={20} />}
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => {
                  if (page !== currentPage) fetchBookings(page, true);
                }}
                color="primary"
                showFirstButton
                showLastButton
                disabled={isLoadingMore}
              />
              <Typography variant="body2" color="textSecondary">
                Page {currentPage} of {totalPages} ({totalCount} total)
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewBooking}>
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleStatusUpdateClick}>
          <Edit sx={{ mr: 1 }} />
          Update Status
        </MenuItem>
        
        {(() => {
          const u = selectedBooking?.user;
          const label = u?._id ? noteMenuLabel(notesMode, (notesByUser[u._id] || []).length > 0) : null;
          return label && u ? (
            <MenuItem
              onClick={() => {
                // Opens the same small popup, at the row's ⋮ button.
                if (anchorEl) {
                  setNoteUser({
                    _id: u._id,
                    name: u.name,
                    studioId: bookingStudioOf(selectedBooking),
                    studioLabel: selectedBooking?.studio?.studioName,
                    anchorEl,
                  });
                }
                handleMenuClose();
              }}
            >
              <StickyNote2Outlined sx={{ mr: 1 }} />
              {label}
            </MenuItem>
          ) : null;
        })()}

        {canReschedule(selectedBooking) && (
          <MenuItem onClick={handleRescheduleClick}>
            <EventRepeat sx={{ mr: 1 }} />
            Reschedule
          </MenuItem>
        )}

        {refundActionable(selectedBooking) && (
          <MenuItem onClick={handleProcessRefund} disabled={refundBusy} sx={{ color: colors.success }}>
            <CurrencyRupee sx={{ mr: 1 }} />
            {selectedBooking?.refundStatus === 'FAILED' ? 'Retry refund' : 'Approve refund'}
            {selectedBooking?.refund?.amount != null && ` (₹${selectedBooking.refund.amount.toLocaleString()})`}
          </MenuItem>
        )}

        {selectedBooking?.refundStatus === 'REQUESTED' && (
          <MenuItem onClick={handleRejectRefundClick} disabled={refundBusy}>
            <Block sx={{ mr: 1 }} />
            Decline refund
          </MenuItem>
        )}

        {isSuperAdmin && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: colors.error }}>
            <Delete sx={{ mr: 1 }} />
            Delete Booking
          </MenuItem>
        )}
      </Menu>

      <UserNotePopover
        anchorEl={noteUser?.anchorEl ?? null}
        user={noteUser}
        studioId={noteUser?.studioId ?? null}
        studioLabel={noteUser?.studioLabel}
        notes={noteUser ? notesByUser[noteUser._id] || [] : []}
        mode={notesMode}
        onClose={() => setNoteUser(null)}
        onChange={updateNote}
      />

      {/* Decline a requested refund */}
      <Dialog open={rejectRefundOpen} onClose={() => setRejectRefundOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Decline refund</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            No money will be sent. The refund is recorded as declined against
            booking {refundTarget?.bookingId ?? ''}, so the decision stays on the record.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            value={rejectRefundReason}
            onChange={(e) => setRejectRefundReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectRefundOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRejectRefund} color="error" disabled={refundBusy}>
            Decline refund
          </Button>
        </DialogActions>
      </Dialog>

      <RescheduleBookingDialog
        open={rescheduleDialogOpen}
        onClose={() => { setRescheduleDialogOpen(false); setBookingToReschedule(null); }}
        onSuccess={() => fetchBookings()}
        booking={bookingToReschedule}
      />

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Filter Bookings</Typography>
            <Button
              onClick={() => setFilterDialogOpen(false)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <Close />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Studio Filter - Searchable Autocomplete (Only for admins) */}
            {!isStudio && (
              <Autocomplete
                fullWidth
                options={studios}
                getOptionLabel={(option) => option.studioName || ''}
                value={studios.find((s) => s._id === studioFilter) || null}
                onChange={(event, newValue) => {
                  setStudioFilter(newValue?._id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Studio"
                    placeholder="Search or select a studio (leave empty for all studios)"
                  />
                )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option._id}>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {option.studioName}
                    </Typography>
                    {option.city && (
                      <Typography variant="caption" color="text.secondary">
                        {option.city}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                noOptionsText="No studios found"
                filterOptions={(options, { inputValue }) => {
                  return options.filter((option) =>
                    option.studioName?.toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.city?.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
              />
            )}

            {/* Date Filter Type */}
            <FormControl fullWidth>
              <InputLabel>Date Filter</InputLabel>
              <Select
                value={dateFilterType}
                label="Date Filter"
                onChange={(e) => handleDateFilterTypeChange(e.target.value as 'none' | 'month' | 'week' | 'custom')}
              >
                <MenuItem value="none">No Date Filter</MenuItem>
                <MenuItem value="month">Month-wise</MenuItem>
                <MenuItem value="week">Weekly</MenuItem>
                <MenuItem value="custom">Custom Date Range</MenuItem>
              </Select>
            </FormControl>

            {/* Month Selector */}
            {dateFilterType === 'month' && (
              <TextField
                label="Select Month"
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}

            {/* Week Selector */}
            {dateFilterType === 'week' && (
              <TextField
                label="Select Week"
                type="week"
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}

            {/* Custom Date Range */}
            {dateFilterType === 'custom' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClearFilters} color="secondary">
            Clear All
          </Button>
          <Button onClick={handleApplyFilters} variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Booking
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this booking? This action cannot be undone.
            {bookingToDelete && (
              <>
                <br /><br />
                <strong>Booking ID:</strong> {bookingToDelete.bookingId || bookingToDelete._id}
                <br />
                <strong>User:</strong> {bookingToDelete.user?.name}
                <br />
                <strong>Studio:</strong> {bookingToDelete.studio?.studioName}
                <br />
                <strong>Status:</strong> {bookingToDelete.bookingStatus}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Booking Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => !isCancelling && setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel color="error" fontSize="small" />
          Cancel Booking
        </DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Booking: <strong>{selectedBooking.bookingId || selectedBooking._id}</strong>
                <br />User: <strong>{selectedBooking.user?.name}</strong>
              </Typography>
              {isPackBooking(selectedBooking) && (<>
              <Typography variant="body2">
                How should the class credit be handled?
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  onClick={() => setCancelCreditBack(true)}
                  sx={{
                    border: '2px solid',
                    borderColor: cancelCreditBack ? 'success.main' : 'divider',
                    borderRadius: 1.5,
                    p: 1.5,
                    cursor: 'pointer',
                    bgcolor: cancelCreditBack ? 'success.50' : 'background.paper',
                    transition: 'all 0.15s',
                  }}
                >
                  <Typography variant="body2" fontWeight={600} color={cancelCreditBack ? 'success.dark' : 'text.primary'}>
                    Credit class back to pack
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    The class will be returned to the user's pack balance
                  </Typography>
                </Box>
                <Box
                  onClick={() => setCancelCreditBack(false)}
                  sx={{
                    border: '2px solid',
                    borderColor: !cancelCreditBack ? 'error.main' : 'divider',
                    borderRadius: 1.5,
                    p: 1.5,
                    cursor: 'pointer',
                    bgcolor: !cancelCreditBack ? 'error.50' : 'background.paper',
                    transition: 'all 0.15s',
                  }}
                >
                  <Typography variant="body2" fontWeight={600} color={!cancelCreditBack ? 'error.dark' : 'text.primary'}>
                    Do not credit back
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    The class is forfeited — pack balance stays unchanged
                  </Typography>
                </Box>
              </Box>
              </>)}
              <TextField
                label="Reason (optional)"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="Reason for cancellation..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={isCancelling}>
            Back
          </Button>
          <Button
            onClick={handleCancelConfirm}
            variant="contained"
            color="error"
            disabled={isCancelling}
          >
            {isCancelling ? 'Cancelling...' : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Paid confirmation */}
      <Dialog open={!!markPaidTarget} onClose={() => !isMarkingPaid && setMarkPaidTarget(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          {markPaidTarget && (() => {
            const t = markPaidTarget;
            const amount = (t.transaction?.basePrice ?? 0) + (t.transaction?.taxes ?? 0);
            const item = t.session?.className || t.event?.eventName;
            const when = t.session?.date || t.event?.date;
            // Session dates are real UTC; event dates need the offset (same as the list).
            const whenText = when ? formatDateIST(when, 'datetime', !t.session && !!t.event) : null;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{t.user?.name || 'Guest'}</Typography>
                  {(item || whenText) && (
                    <Typography variant="caption" color="text.secondary">
                      {[item, whenText].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'action.hover',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">Collected at studio</Typography>
                  <Typography variant="body1" fontWeight={700}>₹{amount.toLocaleString('en-IN')}</Typography>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidTarget(null)} disabled={isMarkingPaid}>
            Back
          </Button>
          <Button onClick={handleMarkPaidConfirm} variant="contained" color="primary" disabled={isMarkingPaid}>
            {isMarkingPaid ? 'Marking...' : 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Booking Status Dialog */}
      <BookingStatusDialog
        open={statusUpdateDialogOpen}
        booking={bookingToUpdateStatus}
        onClose={() => setStatusUpdateDialogOpen(false)}
        onUpdated={handleStatusUpdated}
        onError={setError}
      />

      {/* Manual Booking Dialog — when opened from a particular session's
          filtered bookings list, prefill that session (and, for super admins
          who operate across studios, its studio too) so "Create Booking"
          books straight into it instead of starting the picker flow over. */}
      <ManualBookingDialog
        open={manualBookingOpen}
        onClose={() => setManualBookingOpen(false)}
        onSuccess={() => { setManualBookingOpen(false); fetchBookings(1, true); setScheduleRefreshKey((n) => n + 1); }}
        studios={studios}
        prefilledSession={viewMode === 'list' && selectedCalendarSession ? selectedCalendarSession : undefined}
        prefilledStudio={viewMode === 'list' && selectedCalendarSession && isSuperAdmin ? selectedCalendarSession.studio : undefined}
      />
    </Box>
  );
};

export default BookingsPage;
